import { useEffect, useState } from "react";
import { Check, Copy, Gift, Loader2, Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { track } from "@/lib/posthog";

type Data = {
  code: string | null;
  isTeam: boolean;
  rewardsLastYear: number;
  designsEarned: number;
  bonusDesignsLeft: number;
  newRewards: { type: string; appliedAs: string | null }[];
  referrals: { status: string; createdAt: string }[];
};

const STATUS_LABEL: Record<string, string> = {
  signed_up: "Joined · +1 design",
  design_done: "Joined · +1 design",
  paid: "Joined · +1 design",
};

const ReferralCard = () => {
  const { toast } = useToast();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    track("referral_page_viewed");
    supabase.functions.invoke<Data>("referrals", { body: { action: "get" } }).then(({ data }) => {
      setData(data ?? null);
      setLoading(false);
      const fresh = data?.newRewards ?? [];
      const credits = fresh.filter((r) => r.type === "design_credit").length;
      if (credits) toast({ title: `You earned ${credits} free design${credits > 1 ? "s" : ""}`, description: "Thanks for inviting friends to Huelo." });
      for (const r of fresh.filter((r) => r.type !== "design_credit")) {
        toast({
          title: r.appliedAs === "stripe_coupon" ? "Your next month is on us" : "You earned a free month of Pro",
          description: "Thanks for inviting friends to Huelo.",
        });
      }
    });
  }, [toast]);

  const link = data?.code ? `${window.location.origin}/r/${data.code}` : "";

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    track("referral_link_copied");
  };
  const share = async () => {
    try {
      await navigator.share({ title: "Huelo", text: "Redesign your room with AI — try Huelo:", url: link });
      track("referral_link_shared");
    } catch { /* cancelled */ }
  };

  if (loading) {
    return <div className="material-card p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }
  if (!data?.code) return null;

  return (
    <section id="invite" className="material-card p-6 space-y-5">
      <div className="flex items-start gap-3">
        <span className="icon-tile shrink-0"><Gift className="w-4 h-4" /></span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground">Invite friends, get free designs</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Get 1 free design for every friend who joins. You get it the moment they sign up, and it never expires.
          </p>
        </div>
      </div>

      {data.isTeam && (
        <p className="text-xs text-muted-foreground">Huelo team accounts can share links but don't earn rewards.</p>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 min-w-0 rounded-full border border-border bg-muted px-4 py-2.5 text-xs text-foreground truncate">{link}</div>
        <div className="flex gap-2">
          <button onClick={copy} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary-strong text-primary-strong-foreground px-5 py-2.5 text-xs font-semibold hover:opacity-90">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy link"}
          </button>
          {typeof navigator !== "undefined" && "share" in navigator && (
            <button onClick={share} aria-label="Share link" className="inline-flex items-center justify-center rounded-full border border-border px-3 py-2.5 text-foreground hover:bg-muted">
              <Share2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="rounded-xl bg-muted p-3">
          <p className="text-lg font-semibold text-foreground">{data.designsEarned}</p>
          <p className="text-[11px] text-muted-foreground">Free designs earned</p>
        </div>
        <div className="rounded-xl bg-muted p-3">
          <p className="text-lg font-semibold text-foreground">{data.bonusDesignsLeft}</p>
          <p className="text-[11px] text-muted-foreground">Free designs left</p>
        </div>
      </div>

      {data.referrals.length > 0 && (
        <ul className="divide-y divide-border border-t border-border">
          {data.referrals.slice(0, 10).map((r, i) => (
            <li key={i} className="flex items-center justify-between py-2.5 text-xs">
              <span className="text-foreground">Friend {data.referrals.length - i}</span>
              <span className="text-muted-foreground">{STATUS_LABEL[r.status] ?? r.status}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ReferralCard;
